import BaseEntity from '@/editor/entities/BaseEntity';
import { cloneDeep } from 'lodash-es';
import { EEntityType } from '../types';
import { equalOutlines } from '../math/utils';
import { RoomData } from '../data/room/Room';
import RoomModel2d from './2d/roomModel';
import RoomModel3d from './3d/roomModel';
import { getPathFromEdges } from '../utils/edge';
import { dispersePath } from '../utils/shape';

class RoomEntity extends BaseEntity<EEntityType.Room> {
  private _roomDataData: RoomData;
  private _withRegion;

  protected override _model2d: RoomModel2d;
  protected override _model3d: RoomModel3d | null = null;

  constructor(roomData: RoomData, withRegion: boolean) {
    super({
      type: EEntityType.Room,
      id: roomData.id
    });
    this._roomDataData = cloneDeep(roomData);
    this._withRegion = withRegion;

    this._model2d = new RoomModel2d(this._roomDataData);
    if (!this._withRegion) {
      this._model3d = new RoomModel3d(this._roomDataData);
    }
  }

  override set selected(value: boolean) {
    this._selected = value;
  }

  override get selected() {
    return this._selected;
  }

  get roomData() {
    return this._roomDataData;
  }

  override update(roomData: RoomData & { forceUpdate?: boolean }, withRegion: boolean) {
    let destroyUpdate = !!roomData.forceUpdate;
    let noDestroyUpdate = false;

    const newOutline = [
      dispersePath(getPathFromEdges(roomData.corePathInfos)),
      ...roomData.coreHoleInfos.map((holes) => dispersePath(getPathFromEdges(holes)))
    ];
    const oldOutline = [
      dispersePath(getPathFromEdges(this._roomDataData.corePathInfos)),
      ...this._roomDataData.coreHoleInfos.map((holes) => dispersePath(getPathFromEdges(holes)))
    ];

    if (!equalOutlines(newOutline, oldOutline)) {
      destroyUpdate = true;
    }

    if (roomData.materialInfo.id !== this._roomDataData.materialInfo.id) {
      noDestroyUpdate = true;
    }

    if (withRegion !== this._withRegion) {
      destroyUpdate = true;
      this._withRegion = withRegion;
    }

    if (!destroyUpdate && !noDestroyUpdate) return;

    this._roomDataData = cloneDeep(roomData);
    this._model2d.updateModel(this._roomDataData);

    if (!this._withRegion) {
      if (this._model3d) {
        this._model3d.createRegionModel(this._roomDataData);
      } else {
        this._model3d = new RoomModel3d(this._roomDataData);
      }
    } else {
      this._model3d?.dispose();
      this._model3d = null;
    }
  }

  changeFloorPlaneStatus(enable: boolean) {
    this._model2d.setFloorPlaneStatus(enable);
  }
}

export default RoomEntity;
